import React, { useEffect, useRef, useState } from 'react';

/**
 * Player audio in stile WhatsApp.
 *
 * Props:
 *  - src (string)         URL del file audio (es. blob: o stream API)
 *  - loading (bool)       mostra spinner sul play (mentre l'URL si carica)
 *  - filename (string)    nome del file (mostrato come tooltip)
 */
const VoiceMessagePlayer = ({ src, loading = false, filename = "vocale" }) => {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;

        const onLoaded = () => {
            const d = a.duration;
            // Alcuni browser danno Infinity per i webm finché non si seek
            if (isFinite(d) && d > 0) setDuration(d);
        };
        const onTimeUpdate = () => setCurrent(a.currentTime || 0);
        const onEnded = () => { setPlaying(false); setCurrent(0); };
        const onPlay = () => setPlaying(true);
        const onPause = () => setPlaying(false);

        a.addEventListener('loadedmetadata', onLoaded);
        a.addEventListener('durationchange', onLoaded);
        a.addEventListener('timeupdate', onTimeUpdate);
        a.addEventListener('ended', onEnded);
        a.addEventListener('play', onPlay);
        a.addEventListener('pause', onPause);

        return () => {
            a.removeEventListener('loadedmetadata', onLoaded);
            a.removeEventListener('durationchange', onLoaded);
            a.removeEventListener('timeupdate', onTimeUpdate);
            a.removeEventListener('ended', onEnded);
            a.removeEventListener('play', onPlay);
            a.removeEventListener('pause', onPause);
        };
    }, [src]);

    const togglePlay = () => {
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) {
            // hack per fix duration Infinity su webm
            if (!isFinite(a.duration) || a.duration === 0) {
                a.currentTime = 1e10;
                a.ontimeupdate = () => {
                    a.ontimeupdate = null;
                    a.currentTime = 0;
                    a.play();
                };
            } else {
                a.play();
            }
        } else {
            a.pause();
        }
    };

    const onSeek = (e) => {
        const a = audioRef.current;
        if (!a) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const ratio = Math.max(0, Math.min(1, clickX / rect.width));
        const d = isFinite(a.duration) ? a.duration : duration;
        if (d > 0) {
            a.currentTime = ratio * d;
            setCurrent(a.currentTime);
        }
    };

    const formatTime = (s) => {
        if (!isFinite(s) || s < 0) s = 0;
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return String(m).padStart(2, '0') + ":" + String(sec).padStart(2, '0');
    };

    const progress = (duration > 0) ? (current / duration) * 100 : 0;
    const showTime = playing ? current : (duration || 0);

    return (
        <div
            className="d-inline-flex align-items-center"
            style={{
                background: "#e9f7ef",
                border: "1px solid #b8e0c8",
                borderRadius: 18,
                padding: "6px 12px",
                minWidth: 220,
                maxWidth: 320,
                gap: 10
            }}
            title={filename}
        >
            <button
                type="button"
                onClick={togglePlay}
                disabled={loading}
                style={{
                    background: "#25D366",
                    border: "none",
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    cursor: loading ? "wait" : "pointer",
                    flexShrink: 0
                }}
                title={playing ? "Pausa" : "Riproduci"}
            >
                {loading ? (
                    <i className="mdi mdi-loading mdi-spin fs-18"></i>
                ) : playing ? (
                    <i className="mdi mdi-pause fs-18"></i>
                ) : (
                    <i className="mdi mdi-play fs-18"></i>
                )}
            </button>

            <div
                onClick={onSeek}
                style={{
                    flexGrow: 1,
                    height: 4,
                    background: "#cdebd6",
                    borderRadius: 2,
                    cursor: "pointer",
                    position: "relative",
                    overflow: "hidden"
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: progress + "%",
                        background: "#25D366",
                        transition: "width 0.1s linear"
                    }}
                />
            </div>

            <span style={{ fontSize: 12, color: "#0e6037", fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                {formatTime(showTime)}
            </span>

            <i className="mdi mdi-microphone" style={{ color: "#25D366", fontSize: 16 }} />

            {src ? (
                <audio ref={audioRef} src={src} preload="metadata" style={{ display: "none" }} />
            ) : null}
        </div>
    );
};

export default VoiceMessagePlayer;
